/**
 * SearchResultRow — V107.52.34
 *
 * Single row rendered inside the global search modal. Type-aware:
 * customer rows get inline call/SMS/navigate action buttons, deal/fault/job
 * rows show stage and status badges, etc.
 *
 * The whole row is a button that fires `onSelect` (close modal + navigate +
 * persist recent search). Action buttons inside customer rows use
 * stopPropagation so tapping 📞 doesn't navigate.
 */

import { getLocaleSync } from '@/utils/locale';
import {
  User,
  Contact as ContactIcon,
  Sparkles,
  AlertTriangle,
  Briefcase,
  MessageSquare,
  Wifi,
  Tag,
  Phone,
  MessageCircle,
  Navigation,
  Mail,
  ChevronRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import type { SearchResultItem } from '@/hooks/useGlobalSearch';
import { FAULT_STAGE_LABELS, FAULT_STAGE_COLORS, migrateFaultStage } from '@/types/fault.types';
import { DEAL_STAGE_LABELS } from '@/types/theo-workflow.types';
import { getServiceAccountNumber } from '@/utils/customer-service-search';
import { formatCurrency as formatTenantCurrency } from '@/utils/pricing';
import { addressSuburb, composeSiteAddress, formatCustomerAddress, formatCustomerLocality } from '@/utils/address';

// ---------------------------------------------------------------------------
// Type-specific config
// ---------------------------------------------------------------------------

const TYPE_CONFIG: Record<
  SearchResultItem['type'],
  { Icon: typeof User; ring: string; bg: string; text: string; label: string }
> = {
  customer: {
    Icon: User,
    ring: 'ring-sky-400/30',
    bg: 'bg-sky-500/10',
    text: 'text-sky-300',
    label: 'Customer',
  },
  contact: {
    Icon: ContactIcon,
    ring: 'ring-teal-400/30',
    bg: 'bg-teal-500/10',
    text: 'text-teal-300',
    label: 'Contact',
  },
  deal: {
    Icon: Sparkles,
    ring: 'ring-amber-400/30',
    bg: 'bg-amber-500/10',
    text: 'text-amber-300',
    label: 'Deal',
  },
  fault: {
    Icon: AlertTriangle,
    ring: 'ring-rose-400/30',
    bg: 'bg-rose-500/10',
    text: 'text-rose-300',
    label: 'Fault',
  },
  job: {
    Icon: Briefcase,
    ring: 'ring-purple-400/30',
    bg: 'bg-purple-500/10',
    text: 'text-purple-300',
    label: 'Job',
  },
  service_request: {
    Icon: MessageSquare,
    ring: 'ring-violet-400/30',
    bg: 'bg-violet-500/10',
    text: 'text-violet-300',
    label: 'Request',
  },
  customer_service: {
    Icon: Wifi,
    ring: 'ring-emerald-400/30',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-300',
    label: 'Service',
  },
  serial: {
    Icon: Tag,
    ring: 'ring-cyan-400/30',
    bg: 'bg-cyan-500/10',
    text: 'text-cyan-300',
    label: 'Serial',
  },
};

// Severity → badge color (faults)
const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-rose-500/15 text-rose-300 border border-rose-400/25',
  high: 'bg-orange-500/15 text-orange-300 border border-orange-400/25',
  medium: 'bg-amber-500/15 text-amber-300 border border-amber-400/25',
  low: 'bg-slate-500/15 text-slate-300 border border-slate-400/25',
};

const PRIORITY_BADGE: Record<string, string> = {
  urgent: 'bg-rose-500/15 text-rose-300 border border-rose-400/25',
  high: 'bg-orange-500/15 text-orange-300 border border-orange-400/25',
  medium: 'bg-amber-500/15 text-amber-300 border border-amber-400/25',
  low: 'bg-slate-500/15 text-slate-300 border border-slate-400/25',
};

// ---------------------------------------------------------------------------
// Customer inline actions
// ---------------------------------------------------------------------------

interface InlineActionProps {
  icon: typeof Phone;
  href: string;
  label: string;
  onActivate?: () => void;
}

function InlineAction({ icon: Icon, href, label, onActivate }: InlineActionProps) {
  return (
    <a
      href={href}
      onClick={(e) => {
        e.stopPropagation();
        onActivate?.();
      }}
      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-surface-hover/60 hover:bg-surface-hover text-theme-secondary hover:text-theme-primary border border-theme-soft hover:border-theme transition-all text-[11px] font-medium"
      aria-label={label}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </a>
  );
}

function googleMapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface Props {
  item: SearchResultItem;
  isSelected: boolean;
  onSelect: () => void;
  onHover?: () => void;
}

export function SearchResultRow({ item, isSelected, onSelect, onHover }: Props) {
  const config = TYPE_CONFIG[item.type];
  const { Icon } = config;

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      onMouseEnter={onHover}
      className={`group w-full text-left rounded-xl border transition-all duration-150 px-3 py-3 sm:py-3.5 flex items-start gap-3 min-h-[64px] ${
        isSelected
          ? 'border-brand-secondary/50 bg-surface-hover shadow-[0_0_0_1px_rgba(197,160,101,0.2)]'
          : 'border-theme-soft bg-surface-card/50 hover:bg-surface-hover hover:border-theme'
      }`}
      whileTap={{ scale: 0.98 }}
    >
      {/* Type icon badge */}
      <div
        className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${config.bg} ${config.text} ring-1 ${config.ring}`}
      >
        <Icon className="w-4 h-4" />
      </div>

      {/* Body — type-specific layout */}
      <div className="flex-1 min-w-0">{renderBody(item)}</div>

      {/* Right chevron */}
      <ChevronRight className="shrink-0 w-4 h-4 mt-2 text-theme-muted group-hover:text-theme-tertiary transition-colors" />
    </motion.button>
  );
}

// ---------------------------------------------------------------------------
// Per-type body renderers
// ---------------------------------------------------------------------------

function renderBody(item: SearchResultItem): React.ReactNode {
  switch (item.type) {
    case 'customer':
      return <CustomerBody item={item} />;
    case 'contact':
      return <ContactBody item={item} />;
    case 'deal':
      return <DealBody item={item} />;
    case 'fault':
      return <FaultBody item={item} />;
    case 'job':
      return <JobBody item={item} />;
    case 'service_request':
      return <ServiceRequestBody item={item} />;
    case 'customer_service':
      return <CustomerServiceBody item={item} />;
    case 'serial':
      return <SerialBody item={item} />;
  }
}

function CustomerBody({ item }: { item: Extract<SearchResultItem, { type: 'customer' }> }) {
  const c = item.customer;
  const displayName =
    c.name || [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.companyName || 'Unknown';
  const subtitleParts: string[] = [];
  if (c.phone) subtitleParts.push(c.phone);
  // Town first — a search row that reads "13 School Ln" tells you nothing when
  // three customers live on a School Ln in three different towns. `city` is
  // blank on plenty of records, so fall back to the tail of the street line,
  // then to the street line itself. Kept short: this row truncates.
  const town = formatCustomerLocality(c) || addressSuburb({ address: c.address });
  if (town || c.address) subtitleParts.push(town || c.address || '');
  if (c.accountNumber) subtitleParts.push(c.accountNumber);

  // Pick primary address for navigate
  // Full address — Navigate on a street line alone routes to whichever
  // matching street Google likes best, which is rarely the right town.
  const primaryAddress =
    composeSiteAddress(c.addresses?.find((a) => a.isPrimary) ?? {}) ||
    composeSiteAddress(c.addresses?.[0] ?? {}) ||
    formatCustomerAddress(c) ||
    '';

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-theme-primary truncate">{displayName}</p>
        {c.isCompany && (
          <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-400/20">
            Company
          </span>
        )}
        {c.parentCompanyName && (
          <span className="text-[10px] text-theme-muted truncate">↳ {c.parentCompanyName}</span>
        )}
      </div>
      {subtitleParts.length > 0 && (
        <p className="text-xs text-theme-tertiary truncate mt-0.5">
          {subtitleParts.join(' · ')}
        </p>
      )}
      {/* Inline actions */}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {c.phone && (
          <InlineAction
            icon={Phone}
            href={`tel:${c.phone.replace(/\s+/g, '')}`}
            label="Call"
          />
        )}
        {c.phone && (
          <InlineAction
            icon={MessageCircle}
            href={`sms:${c.phone.replace(/\s+/g, '')}`}
            label="SMS"
          />
        )}
        {primaryAddress && (
          <InlineAction
            icon={Navigation}
            href={googleMapsUrl(primaryAddress)}
            label="Navigate"
          />
        )}
      </div>
    </div>
  );
}

function ContactBody({ item }: { item: Extract<SearchResultItem, { type: 'contact' }> }) {
  const ct = item.contact;
  const roleBits: string[] = [];
  if (ct.role) roleBits.push(ct.role);
  if (ct.isPrimaryAccount || ct.contactType?.includes('account')) roleBits.push('Account');
  if (ct.isPrimarySite || ct.contactType?.includes('site')) roleBits.push('Site');
  const roleLabel = roleBits[0];

  const subtitleParts: string[] = [];
  if (item.customerName) subtitleParts.push(item.customerName);
  if (ct.phone) subtitleParts.push(ct.phone);

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-theme-primary truncate">{ct.name || 'Contact'}</p>
        {roleLabel && (
          <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-300 border border-teal-400/20">
            {roleLabel}
          </span>
        )}
      </div>
      {item.customerName && (
        <p className="text-[11px] text-theme-muted truncate mt-0.5">↳ {item.customerName}</p>
      )}
      {subtitleParts.length > 1 && (
        <p className="text-xs text-theme-tertiary truncate mt-0.5">{ct.phone}</p>
      )}
      {/* Inline actions */}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {ct.phone && (
          <InlineAction icon={Phone} href={`tel:${ct.phone.replace(/\s+/g, '')}`} label="Call" />
        )}
        {ct.phone && (
          <InlineAction icon={MessageCircle} href={`sms:${ct.phone.replace(/\s+/g, '')}`} label="SMS" />
        )}
        {ct.email && <InlineAction icon={Mail} href={`mailto:${ct.email}`} label="Email" />}
      </div>
    </div>
  );
}

function DealBody({ item }: { item: Extract<SearchResultItem, { type: 'deal' }> }) {
  const d = item.deal;
  const stageLabel = (DEAL_STAGE_LABELS as Record<string, string>)[String(d.stage)] || String(d.stage);
  const value = typeof d.value === 'number' ? formatCurrency(d.value) : null;
  const subtitleParts: string[] = [];
  if (stageLabel) subtitleParts.push(stageLabel);
  if (value) subtitleParts.push(value);
  if (d.category) subtitleParts.push(String(d.category).replace(/_/g, ' ').toLowerCase());

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-theme-primary truncate">
          {d.customerName || d.title || 'Untitled deal'}
        </p>
        {item.isActive && (
          <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-400/20">
            Active
          </span>
        )}
      </div>
      {d.title && d.title !== d.customerName && (
        <p className="text-[11px] text-theme-tertiary truncate mt-0.5">{d.title}</p>
      )}
      <p className="text-xs text-theme-tertiary truncate mt-0.5">{subtitleParts.join(' · ')}</p>
    </div>
  );
}

function FaultBody({ item }: { item: Extract<SearchResultItem, { type: 'fault' }> }) {
  const f = item.fault;
  // Route through migrateFaultStage — legacy docs carry old stage strings
  const stage = migrateFaultStage(f.stage);
  const stageLabel = FAULT_STAGE_LABELS[stage] || f.stage;
  const stageColor = FAULT_STAGE_COLORS[stage]?.text || 'text-theme-tertiary';
  const subtitleParts: string[] = [];
  if (f.ticketNumber) subtitleParts.push(f.ticketNumber);
  if (f.serviceType) subtitleParts.push(f.serviceType);
  if (f.address) subtitleParts.push(f.address);

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-theme-primary truncate">{f.clientName}</p>
        {f.severity && (
          <span
            className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium ${
              SEVERITY_BADGE[f.severity] || SEVERITY_BADGE.low
            }`}
          >
            {f.severity}
          </span>
        )}
        {item.isActive && (
          <span className={`text-[9px] uppercase tracking-wide font-medium ${stageColor}`}>
            {stageLabel}
          </span>
        )}
        {!item.isActive && (
          <span className="text-[9px] uppercase tracking-wide text-theme-muted">Resolved</span>
        )}
      </div>
      {f.symptom && (
        <p className="text-xs text-theme-secondary truncate mt-0.5">{f.symptom}</p>
      )}
      {subtitleParts.length > 0 && (
        <p className="text-[11px] text-theme-tertiary truncate mt-0.5">
          {subtitleParts.join(' · ')}
        </p>
      )}
    </div>
  );
}

function JobBody({ item }: { item: Extract<SearchResultItem, { type: 'job' }> }) {
  const j = item.job;
  const subtitleParts: string[] = [];
  if (j.type) subtitleParts.push(j.type);
  if (j.status) subtitleParts.push(j.status);
  if (j.date) subtitleParts.push(formatDateShort(j.date) + (j.time ? ` ${j.time}` : ''));

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-theme-primary truncate">
          {j.client || j.linkedCustomerName || 'Job'}
        </p>
        {item.isActive && (
          <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-400/20">
            Today
          </span>
        )}
      </div>
      {j.address && (
        <p className="text-xs text-theme-tertiary truncate mt-0.5">{j.address}</p>
      )}
      {subtitleParts.length > 0 && (
        <p className="text-[11px] text-theme-tertiary truncate mt-0.5">
          {subtitleParts.join(' · ')}
        </p>
      )}
    </div>
  );
}

function ServiceRequestBody({ item }: { item: Extract<SearchResultItem, { type: 'service_request' }> }) {
  const r = item.request;
  const subtitleParts: string[] = [];
  if (r.ticketNumber) subtitleParts.push(r.ticketNumber);
  if (r.serviceType) subtitleParts.push(r.serviceType);
  if (r.requestType) subtitleParts.push(String(r.requestType).replace(/_/g, ' '));

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-theme-primary truncate">{r.customerName}</p>
        {r.priority && (
          <span
            className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium ${
              PRIORITY_BADGE[r.priority] || PRIORITY_BADGE.low
            }`}
          >
            {r.priority}
          </span>
        )}
        {!r.archived && (
          <span className="text-[9px] uppercase tracking-wide text-violet-300">Open</span>
        )}
      </div>
      {r.title && (
        <p className="text-xs text-theme-secondary truncate mt-0.5">{r.title}</p>
      )}
      {subtitleParts.length > 0 && (
        <p className="text-[11px] text-theme-tertiary truncate mt-0.5">
          {subtitleParts.join(' · ')}
        </p>
      )}
    </div>
  );
}

function CustomerServiceBody({ item }: { item: Extract<SearchResultItem, { type: 'customer_service' }> }) {
  const s = item.service;
  const accountNumber = getServiceAccountNumber(s);
  const subtitleParts: string[] = [];
  if (s.serviceType) subtitleParts.push(s.serviceType);
  if (s.planName) subtitleParts.push(s.planName);
  if (typeof s.monthlyPrice === 'number' && s.monthlyPrice > 0) {
    subtitleParts.push(`${formatCurrency(s.monthlyPrice)}/mo`);
  }

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-theme-primary truncate">{s.customerName}</p>
        <span
          className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium ${
            s.status === 'Active'
              ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-400/20'
              : s.status === 'Pending Install'
                ? 'bg-amber-500/10 text-amber-300 border border-amber-400/20'
                : s.status === 'Suspended'
                  ? 'bg-orange-500/10 text-orange-300 border border-orange-400/20'
                  : 'bg-slate-500/10 text-slate-300 border border-slate-400/20'
          }`}
        >
          {s.status}
        </span>
      </div>
      {subtitleParts.length > 0 && (
        <p className="text-xs text-theme-tertiary truncate mt-0.5">
          {subtitleParts.join(' · ')}
        </p>
      )}
      {accountNumber && (
        <p className="text-[11px] text-theme-muted truncate mt-0.5">
          Acct: <span className="font-mono text-theme-tertiary">{accountNumber}</span>
        </p>
      )}
      {s.address && (
        <p className="text-[11px] text-theme-muted truncate mt-0.5">{s.address}</p>
      )}
    </div>
  );
}

function SerialBody({ item }: { item: Extract<SearchResultItem, { type: 'serial' }> }) {
  const sn = item.serial;
  const subtitleParts: string[] = [];
  if (sn.itemName) subtitleParts.push(sn.itemName);
  if (sn.customerName) subtitleParts.push(sn.customerName);
  if (sn.supplier) subtitleParts.push(sn.supplier);

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-mono font-semibold text-theme-primary truncate">
          {sn.serialNumber}
        </p>
      </div>
      {subtitleParts.length > 0 && (
        <p className="text-xs text-theme-tertiary truncate mt-0.5">
          {subtitleParts.join(' · ')}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(n: number): string {
  return formatTenantCurrency(n, { maximumFractionDigits: 0 });
}

function formatDateShort(yyyymmdd: string): string {
  // Parse local-time so we don't shift days due to UTC.
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  if (!y || !m || !d) return yyyymmdd;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(getLocaleSync(), { day: 'numeric', month: 'short' });
}
